import { useCreateCandidateBatchGroupFromCandidateBatchMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import Yup from 'utils/validation';
import { useLoadingOverlay } from 'services/loading/hooks';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { Paths } from 'services/navigation/paths';
import { RouterLink } from 'common/ui/RouterLink';
import { FailedMessageWithHref } from 'common/ui/FailedMessageWithHref';
import { Typography } from '@mui/material';

export const useAnalyseDetailsCreateRuleset = () => {
  const [createCandidateBatchGroupFromCandidateBatchMutation] =
    useCreateCandidateBatchGroupFromCandidateBatchMutation();

  const { openModal } = useModalV2();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();

  const onCreateRulesetClick = ({
    title,
    candidateBatchId
  }: {
    title: string;
    candidateBatchId: string;
  }) => {
    openModal({
      title: 'Create ruleset?',
      subtitle:
        'Use the requirements, rules and actions from this analysis to create a new ruleset',
      width: '380px',
      form: {
        schema: Yup.object().shape({
          rulesetName: Yup.string().required('Ruleset name is required')
        }),
        fields: {
          rulesetName: {
            type: 'text',
            label: 'Ruleset Name'
          }
        },
        defaultValues: {
          rulesetName: `${title} ruleset`
        }
      },
      primaryButtonText: 'Confirm',
      onPrimaryAction: async (values) => {
        try {
          showLoadingOverlay();

          if (!candidateBatchId) {
            showToast({
              message: 'Something went wrong',
              type: ToastType.Error
            });
            return;
          }

          await createCandidateBatchGroupFromCandidateBatchMutation({
            variables: {
              input: {
                name: values.rulesetName,
                candidateBatchId: candidateBatchId
              }
            }
          });

          showToast({
            title: 'Ruleset created',
            message: (
              <Typography variant="body2" mt={1} color="text.primary">
                View this new ruleset on the{' '}
                <RouterLink to={Paths.ANALYSE_RULESETS}>
                  ruleset listing screen.
                </RouterLink>
              </Typography>
            ),
            type: ToastType.Success
          });
        } catch (error) {
          showToast({
            title: 'Something went wrong',
            message: (
              <FailedMessageWithHref
                failedMessage=""
                emailSubject="Failed to create ruleset"
              />
            ),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  return {
    onCreateRulesetClick
  };
};
